<template>
  <TheTemplateM class="p-the-page-m04">
    <template #heading>個人情報登録</template>

    <template #body>
      <div class="p-the-page-m04__form c-form-inner">
        <form
          v-if="loaded"
          ref="form"
          action="/my-page/"
          method="put"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <div class="p-form-group">
            <FormLabel :required="true">名前</FormLabel>
            <FormUneditableText>{{ name }}</FormUneditableText>
          </div>

          <div class="p-form-group">
            <FormLabel for="zipcode" :required="true">郵便番号</FormLabel>
            <FormZipcode>
              <InputText
                id="zipcode"
                v-model="fields.zipcode.value"
                type="tel"
                name="zipcode"
                :error="fields.zipcode.errorMessage"
                placeholder="0000000"
                autocomplete="postal-code"
                maxlength="8"
              />
            </FormZipcode>
            <ValidationErrorText v-show="fields.zipcode.errorMessage">
              {{ fields.zipcode.errorMessage }}
            </ValidationErrorText>
          </div>

          <div class="p-form-group">
            <FormLabel for="pref" :required="true">都道府県名</FormLabel>
            <InputSelect
              id="pref"
              v-model="fields.pref.value"
              name="pref"
              :error="fields.pref.errorMessage"
              :options="prefOptions"
              class="p-the-page-m04__pref"
              autocomplete="address-level1"
            />
            <ValidationErrorText v-show="fields.pref.errorMessage">
              {{ fields.pref.errorMessage }}
            </ValidationErrorText>
          </div>

          <div class="p-form-group">
            <FormLabel for="address3" :required="true">市町村区</FormLabel>
            <InputText
              id="address3"
              v-model="fields.address3.value"
              type="text"
              name="address3"
              :error="fields.address3.errorMessage"
              autocomplete="address-level2"
            />
            <ValidationErrorText v-show="fields.address3.errorMessage">
              {{ fields.address3.errorMessage }}
            </ValidationErrorText>
          </div>

          <div class="p-form-group">
            <FormLabel for="address1" :required="true">番地</FormLabel>
            <InputText
              id="address1"
              v-model="fields.address1.value"
              type="text"
              name="address1"
              :error="fields.address1.errorMessage"
              autocomplete="address-level3"
            />
            <ValidationErrorText v-show="fields.address1.errorMessage">
              {{ fields.address1.errorMessage }}
            </ValidationErrorText>
          </div>

          <div class="p-form-group">
            <FormLabel for="address2">建物名・部屋番号</FormLabel>
            <InputText
              id="address2"
              v-model="fields.address2.value"
              type="text"
              name="address2"
              :error="fields.address2.errorMessage"
              autocomplete="address-level4"
            />
            <ValidationErrorText v-show="fields.address2.errorMessage">
              {{ fields.address2.errorMessage }}
            </ValidationErrorText>
          </div>

          <div class="p-form-group">
            <FormLabel for="telephone" :required="true">電話番号</FormLabel>
            <InputText
              id="telephone"
              v-model="fields.telephone.value"
              type="tel"
              name="telephone"
              :error="fields.telephone.errorMessage"
              autocomplete="tel"
            />
            <ValidationErrorText v-show="fields.telephone.errorMessage">
              {{ fields.telephone.errorMessage }}
            </ValidationErrorText>
          </div>

          <div class="p-form-button-group">
            <div class="p-form-button-group__vertical">
              <ButtonMain
                type="submit"
                :disabled="!meta.valid"
                @click-button="submitAddress"
              >
                登録
              </ButtonMain>
            </div>
            <div class="p-form-button-group__vertical">
              <ButtonMain :tag="'a'" href="/my-page/" :kind="'cancel'">
                キャンセル
              </ButtonMain>
            </div>
          </div>
        </form>
      </div>
    </template>
  </TheTemplateM>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { useForm, useField, FormContext } from "vee-validate";
import * as yup from "yup";

import { prefs, getYubinBango } from "@/common/assets/js/utils/address";
import TheTemplateM from "@/general/components/templates/TheTemplateM.vue";
import InputText from "@/common/components/atoms/InputText.vue";
import InputSelect from "@/common/components/atoms/InputSelect.vue";
import ButtonMain from "@/common/components/atoms/ButtonMain.vue";
import FormLabel from "@/common/components/organisms/FormLabel.vue";
import FormZipcode from "@/common/components/organisms/FormZipcode.vue";
import FormUneditableText from "@/common/components/atoms/FormUneditableText.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";

import commons from "@/common/store/modules/commons";
import {
  getPersonalAddressDetails,
  getPersonalAddressUpdate
} from "@/general/store/api";
import LocalStorage from "@/general/utils/storage";
import AuthStorage from "@/general/utils/auth";

@Options({
  components: {
    TheTemplateM,
    InputText,
    InputSelect,
    ButtonMain,
    FormLabel,
    FormZipcode,
    FormUneditableText,
    ValidationErrorText
  },
  watch: {
    zipcode() {
      getYubinBango(this, "zipcode", "pref", "address1", "address2");
    }
  }
})
export default class PersonalInfoUpdate extends Vue {
  name = "";
  userInfo: any = {};
  id = 0;
  loaded = false;
  prefOptions = [{ value: "", label: "都道府県を選択してください" }, ...prefs];

  form!: FormContext;
  fields!: Record<string, ReturnType<typeof useField>>;

  breadcrumb = [
    { text: "", url: "/home/", pageType: "home" },
    { text: "マイページ", url: "/my-page/", pageType: "lower" },
    { text: "個人情報登録", url: "/", pageType: "lower" }
  ];

  mounted() {
    AuthStorage.checkLogin();
  }

  async created() {
    // バリデーションスキーマの定義
    const schema = yup.object({
      zipcode: yup
        .string()
        .required("郵便番号を入力してください。")
        .max(8),
      pref: yup.string().required("都道府県名を入力してください。"),
      address3: yup
        .string()
        .required("市町村区を入力してください。")
        .max(100),
      address1: yup
        .string()
        .required("番地を入力してください。")
        .max(100),
      address2: yup.string().max(100),
      telephone: yup
        .string()
        .required("電話番号を入力してください。")
        .max(15)
    });

    // useForm と useField を初期化
    this.form = useForm({ validationSchema: schema });
    this.fields = {
      zipcode: useField("zipcode"),
      pref: useField("pref"),
      address3: useField("address3"),
      address1: useField("address1"),
      address2: useField("address2"),
      telephone: useField("telephone")
    };

    // ユーザー情報の取得
    await commons.setOrgPageSpecificDataFromLocalStorage();
    const responseGet = await getPersonalAddressDetails();
    this.userInfo = JSON.parse(LocalStorage.getItem("userDetails") as string);
    this.name = commons.getNameFromUser(this.userInfo);

    if (Array.isArray(responseGet?.results) && responseGet.results.length > 0) {
      const address = responseGet.results[0];
      this.id = address.id;
      this.fields.zipcode.value.value = address.zip_code ?? "";
      this.fields.pref.value.value = address.prefecture ?? "";
      this.fields.address1.value.value = address.address_line_1 ?? "";
      this.fields.address2.value.value = address.address_line_2 ?? "";
      this.fields.address3.value.value = address.municipality ?? "";
      this.fields.telephone.value.value = address.phone_number ?? "";
      this.loaded = true;
    }

    await commons.getBreadcrumb(this.breadcrumb);
  }

  async submitAddress() {
    const payload = {
      name: "",
      id: this.id,
      user: this.userInfo.id,
      zip_code: this.fields.zipcode.value.value as string,
      prefecture: this.fields.pref.value.value as string,
      municipality: this.fields.address3.value.value as string,
      phone_number: this.fields.telephone.value.value as string,
      address_line_1: this.fields.address1.value.value as string,
      address_line_2: this.fields.address2.value.value as string
    };

    await getPersonalAddressUpdate(this.id, payload, "");
    window.location.href = "/my-page/personalInformationUpdated.html";
  }

  async onSubmit() {
    await this.submitAddress();
  }

  get meta() {
    return this.form.meta;
  }

  get handleSubmit() {
    return this.form.handleSubmit;
  }

  get zipcode() {
    return this.fields.zipcode.value.value;
  }
}
</script>

<style lang="scss" scoped>
.p-the-page-m04 {
  &__pref {
    width: rem(256);
  }
}
</style>
